import {Table} from '@components/commons/table/Table.tsx'
import {TableBodyCell, TableBodyCellText, TablePagination} from '@components/ui/table-atoms/TableAtoms.tsx'
import {Flexbox} from '@components/ui/flexbox/FlexBox.tsx'
import {Measure, SUB_TYPES} from '@/features/measures/services/types.ts'
import {StyledLink, StyledOmissisBadge} from '@pages/measures/style.ts'
import {StyledLockIcon} from '@pages/measure-details/style.ts'
import {Chip} from '@components/ui/chip/Chip.tsx'
import {
    ArrowRightCircleIcon,
    CalendarIcon,
    FileTextIcon,
    HelpCircleIcon,
    MapPinIcon
} from '@components/ui/icon/Icon.tsx'
import {Button} from '@components/ui/button/Button.tsx'
import {routes} from '@utilities/constants/routes.tsx'
import {useTheme} from 'styled-components'
import {useNavigate} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {FC, useState} from 'react'
import dayjs from 'dayjs'
import {MetaProps} from '@/types.ts'
import {MeasureType} from '@/features/measures/components/measure-type/MeasureType.tsx'
import {capitalize, copyToClipboard, formatGeneralRoleNumber} from '@utilities/helpers.ts'
import {BrowserEmbedded} from '@/features/measures/components/browser-embedded/BrowserEmbedded.tsx'
import {Spinner} from '@components/ui/spinner/Spinner.tsx'
import {useGetMeasureRawHtml} from '@/features/measure-details/services/useGetMeasureRawHtml.ts'
import {Tooltip} from '@components/ui/tooltip/Tooltip.tsx'
import {EmptySearch} from '@components/commons/empty-search/EmptySearch.tsx'
import toast from 'react-hot-toast'

interface MeasureTableDesktopProps {
    measures: Measure[]
    isLoading: boolean
    meta: MetaProps
    setCurrentPage: (page: number) => void
    perPage: number
    setPerPage: (perPage: number) => void
    sorter: {
        orderBy: 'publishedAt' | 'headQuarterLocation' | null
        orderDirection: 'asc' | 'desc'
        onSort: (orderBy: 'publishedAt' | 'headQuarterLocation' | null, orderDirection: 'asc' | 'desc') => void
    }
}

export const MeasureTableDesktop: FC<MeasureTableDesktopProps> = ({
    measures,
    isLoading,
    meta,
    setCurrentPage,
    perPage,
    setPerPage,
    sorter
}) => {
    const theme = useTheme()
    const navigate = useNavigate()
    const {t} = useTranslation()

    const [measureContent, setMeasureContent] = useState<string | null>(null)
    const [loadingMeasures, setLoadingMeasures] = useState<Record<string, boolean>>({})
    const [selectedMeasureId, setSelectedMeasureId] = useState<string | null>(null)

    const measureQuery = useGetMeasureRawHtml()

    const handleFetchMeasure = (id: string) => {
        setLoadingMeasures(prev => ({...prev, [id]: true}))
        setSelectedMeasureId(id)

        measureQuery.mutate(id, {
            onSuccess: data => {
                setMeasureContent(data?.data)
            },
            onSettled: () => {
                setLoadingMeasures(prev => ({...prev, [id]: false}))
            }
        })
    }

    if (!measures.length && !isLoading) {
        return (
            <EmptySearch
                icon={<FileTextIcon fill="none" />}
                title={t('measures:no_measures')}
                subtitle={t('measures:try_different_filters')}
            />
        )
    }

    const handleCopy = (text: string | undefined) => {
        if (text) {
            copyToClipboard(text)
            toast.success(t('commons:copy_success'))
        } else {
            toast.error(t('commons:copy_failed'))
        }
    }

    return (
        <>
            <Table
                isLoading={isLoading}
                sorter={{
                    sortName: sorter.orderBy,
                    direction: sorter.orderDirection,
                    onSort: (sortName, direction) =>
                        sorter.onSort(sortName as 'publishedAt' | 'headQuarterLocation', direction ?? 'desc')
                }}
                data={measures}
                columns={[
                    {
                        header: () => (
                            <Flexbox align={'center'} gap={1}>
                                {t('measures:table:type_of_measure')}
                                <Tooltip content={t('measures:type_of_measure_tooltip')}>
                                    <HelpCircleIcon size={16} />
                                </Tooltip>
                            </Flexbox>
                        ),
                        cell: ({dataItem, forwardedProps}) => (
                            <TableBodyCell {...forwardedProps}>
                                <TableBodyCellText>
                                    <Flexbox align={'center'} gap={2}>
                                        <MeasureType type={dataItem?.subType as SUB_TYPES} />
                                        {dataItem?.isOmissis && (
                                            <Tooltip content={t('measures:table:omissis_tooltip')}>
                                                <StyledOmissisBadge align={'center'}>
                                                    <StyledLockIcon />
                                                </StyledOmissisBadge>
                                            </Tooltip>
                                        )}
                                    </Flexbox>
                                </TableBodyCellText>
                            </TableBodyCell>
                        ),
                        id: 'type_of_measure',
                        size: (tableWidth: number) => Math.floor((tableWidth - 280) * 0.25)
                    },
                    {
                        header: () => t('measures:table:site'),
                        cell: ({dataItem, forwardedProps}) => (
                            <TableBodyCell {...forwardedProps}>
                                <TableBodyCellText>
                                    <Flexbox align={'center'} gap={3}>
                                        <Chip icon={<MapPinIcon />} />
                                        <span>
                                            {capitalize(dataItem?.headquarterLocation?.keyName.replace(/_/g, ' '))}
                                        </span>
                                    </Flexbox>
                                </TableBodyCellText>
                            </TableBodyCell>
                        ),
                        id: 'site',
                        sortName: 'headQuarterLocation',
                        size: (tableWidth: number) => Math.floor((tableWidth - 280) * 0.25)
                    },
                    {
                        header: () => t('measures:table:measure_number'),
                        cell: ({dataItem, forwardedProps}) => (
                            <TableBodyCell {...forwardedProps}>
                                <TableBodyCellText>
                                    <span
                                        onClick={() => handleCopy(dataItem?.measureNumber?.toString())}
                                        style={{
                                            cursor: 'pointer'
                                        }}
                                    >
                                        {dataItem?.measureNumber}
                                    </span>
                                </TableBodyCellText>
                            </TableBodyCell>
                        ),
                        id: 'measure_number',
                        size: (tableWidth: number) => Math.floor((tableWidth - 280) * 0.15)
                    },
                    {
                        header: () => t('measures:table:general_role_number'),
                        cell: ({dataItem, forwardedProps}) => (
                            <TableBodyCell {...forwardedProps}>
                                <TableBodyCellText>
                                    {!dataItem?.appealMeasureId ? (
                                        <span
                                            onClick={() =>
                                                handleCopy(formatGeneralRoleNumber(dataItem?.appealNumber?.toString()))
                                            }
                                            style={{
                                                cursor: 'pointer'
                                            }}
                                        >
                                            {formatGeneralRoleNumber(dataItem?.appealNumber?.toString())}
                                        </span>
                                    ) : (
                                        <StyledLink
                                            href={`${routes.MEASURE_DETAILS.path.replace(':id', dataItem?.appealMeasureId)}`}
                                            target={'_blank'}
                                        >
                                            {formatGeneralRoleNumber(dataItem?.appealNumber?.toString())}
                                        </StyledLink>
                                    )}
                                </TableBodyCellText>
                            </TableBodyCell>
                        ),
                        id: 'general_role_number',
                        size: (tableWidth: number) => Math.floor((tableWidth - 280) * 0.15)
                    },
                    {
                        header: () => t('measures:table:publication_date'),
                        cell: ({dataItem, forwardedProps}) => (
                            <TableBodyCell {...forwardedProps}>
                                <TableBodyCellText>
                                    <Flexbox align={'center'} gap={3}>
                                        <Chip
                                            icon={<CalendarIcon />}
                                            textColor={theme.palette.success['600']}
                                            bgColor={theme.palette.success['100']}
                                        />
                                        <span>
                                            {dataItem?.publishedAt
                                                ? dayjs(dataItem?.publishedAt).format('DD/MM/YYYY')
                                                : '-'}
                                        </span>
                                    </Flexbox>
                                </TableBodyCellText>
                            </TableBodyCell>
                        ),
                        id: 'publication_date',
                        sortName: 'publishedAt',
                        size: (tableWidth: number) => Math.floor((tableWidth - 280) * 0.25)
                    },
                    {
                        header: () => '',
                        cell: ({dataItem, forwardedProps}) => (
                            <TableBodyCell {...forwardedProps}>
                                <Flexbox gap={3} align={'center'} justify={'center'}>
                                    <Button variant={'primaryLight'} onClick={() => handleFetchMeasure(dataItem.id)}>
                                        {loadingMeasures[dataItem.id] ? (
                                            <Spinner size={10} />
                                        ) : (
                                            <FileTextIcon size={20} />
                                        )}
                                    </Button>
                                    <Button
                                        variant={'primaryLight'}
                                        onClick={() =>
                                            navigate(`${routes.MEASURE_DETAILS.path.replace(':id', dataItem.id)}`, {
                                                state: {
                                                    from: `${location.pathname}${location.search}`
                                                }
                                            })
                                        }
                                    >
                                        <ArrowRightCircleIcon size={20} />
                                    </Button>
                                </Flexbox>
                            </TableBodyCell>
                        ),
                        id: 'actions',
                        size: () => 80
                    }
                ]}
            />
            <TablePagination
                isLoading={isLoading}
                pagination={{
                    limit: perPage,
                    currentPage: meta?.currentPage || 1,
                    total: meta?.total || 100,
                    onChangePage: setCurrentPage,
                    onChangeLimit: setPerPage
                }}
            />

            {measureContent && (
                <BrowserEmbedded
                    isOpen={Boolean(measureContent)}
                    onClose={() => setMeasureContent(null)}
                    htmlContent={measureContent}
                    isOmissis={measures.find(m => m.id === selectedMeasureId)?.isOmissis}
                    measureType={measures.find(m => m.id === selectedMeasureId)?.subType?.toLowerCase()}
                />
            )}
        </>
    )
}
